import { supabase } from '../lib/supabase';
import { videoHighlightsService } from './videoHighlightsService';
import { videosService } from './videosService';
import { videoProcessingJobsService } from './videoProcessingJobsService';

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface SocialMediaIntegration {
  id: string;
  org_id: string | null;
  parent_level: boolean;
  platform: string;
  platform_account_id: string;
  platform_account_name: string;
  platform_account_thumbnail_url: string | null;
  display_name: string;
  access_token_encrypted: string;
  refresh_token_encrypted: string | null;
  token_expires_at: string;
  platform_specific_config: Record<string, any> | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface UploadMetadata {
  title: string;
  description: string;
  tags: string[];
  privacyStatus: 'public' | 'unlisted' | 'private';
  playlistId?: string;
  categoryId?: string | null;
  isShort?: boolean; // For YouTube Shorts - when true, categoryId should be null
  [key: string]: any;
}

export interface SocialMediaUpload {
  id: string;
  highlight_id: string | null;
  video_id: string | null;
  match_id: string | null;
  social_media_integration_id: string;
  org_id: string;
  platform: string;
  platform_video_id: string | null;
  platform_url: string | null;
  upload_status: 'pending' | 'queued' | 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled';
  metadata: UploadMetadata;
  error_message: string | null;
  retry_count: number;
  upload_progress: number;
  upload_started_at: string | null;
  upload_completed_at: string | null;
  created_at: string;
  updated_at: string;
  social_media_integrations?: {
    display_name?: string;
    platform_account_name?: string;
  } | null;
}

/**
 * Service for managing social media integrations and uploads
 */
class SocialMediaService {
  /**
   * Get all integrations for an organization (org-level only)
   */
  async getOrgIntegrations(
    orgId: string,
    platform?: string
  ): Promise<ServiceResult<SocialMediaIntegration[]>> {
    try {
      let query = supabase
        .from('social_media_integrations')
        .select('*')
        .eq('org_id', orgId)
        .eq('parent_level', false)
        .eq('is_active', true)
        .neq('platform_account_id', 'placeholder'); // Exclude placeholder records

      if (platform) {
        query = query.eq('platform', platform);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        return {
          success: false,
          error: error.message || 'Failed to fetch org integrations'
        };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getOrgIntegrations failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get integration for upload
   * Phase B: Checks org accounts first, falls back to parent if no org accounts exist
   */
  async getIntegrationForUpload(
    orgId: string,
    platform: 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'twitter'
  ): Promise<ServiceResult<SocialMediaIntegration>> {
    try {
      // Phase B: Check if org has any active integrations for this platform
      const orgIntegrationsResult = await this.getOrgIntegrations(orgId, platform);

      if (orgIntegrationsResult.success && orgIntegrationsResult.data && orgIntegrationsResult.data.length > 0) {
        // Org has accounts - return first active one
        // Note: UI will handle account selection if multiple exist
        return { success: true, data: orgIntegrationsResult.data[0] };
      }

      // Fallback to parent account if org has no accounts
      const parentResult = await this.getParentIntegration(platform);
      if (parentResult.success && parentResult.data) {
        // Check if parent account is active and has real tokens
        if (parentResult.data.is_active && parentResult.data.platform_account_id !== 'placeholder') {
          return { success: true, data: parentResult.data };
        }
      }

      return {
        success: false,
        error: `No active ${platform} integration found. Connect an org account in Social Media Integrations, or ensure the Kridah Labs parent account is set up (Control Room → Parent Social Media Settings).`
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getIntegrationForUpload failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get parent integration (admin only)
   */
  async getParentIntegration(
    platform: string
  ): Promise<ServiceResult<SocialMediaIntegration>> {
    try {
      const { data, error } = await supabase
        .from('social_media_integrations')
        .select('*')
        .eq('parent_level', true)
        .eq('platform', platform)
        .single();

      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Parent integration not found'
        };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getParentIntegration failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Create upload record + job (atomic operation with rollback)
   * Supports both highlights and videos
   */
  async createUploadRecord(
    entityId: string,
    entityType: 'highlight' | 'video',
    matchId: string | null,
    orgId: string,
    integrationId: string,
    platform: string,
    metadata: UploadMetadata,
    videoType?: 'stitched' | 'enhanced' // Optional, only for highlights
  ): Promise<ServiceResult<{ uploadId: string; jobId: string }>> {
    let uploadId: string | null = null;

    try {
      // Normalize optional match ID: empty/whitespace should be null for UUID column.
      const trimmedMatchId = typeof matchId === 'string' ? matchId.trim() : '';
      const normalizedMatchId = trimmedMatchId.length > 0 ? trimmedMatchId : null;
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (normalizedMatchId && !uuidRegex.test(normalizedMatchId)) {
        return { success: false, error: 'Invalid match ID format' };
      }

      if (entityType === 'highlight') {
        // Highlight validation logic
        const highlightResult = await videoHighlightsService.getHighlightById(entityId);
        if (!highlightResult.success || !highlightResult.data) {
          return { success: false, error: 'Highlight not found' };
        }

        const highlight = highlightResult.data;

        // Allow uploads for both 'stitched' (base video ready) and 'completed' (final video with overlays)
        const uploadableStatuses: Array<'stitched' | 'completed'> = ['stitched', 'completed'];
        if (!uploadableStatuses.includes(highlight.status as 'stitched' | 'completed')) {
          return { success: false, error: 'Video processing not complete' };
        }

        // Check if the selected video type is available
        const selectedVideoType = videoType || 'stitched';
        const selectedPath = selectedVideoType === 'enhanced' 
          ? highlight.enhanced_storage_path 
          : highlight.output_storage_path;
        
        if (!selectedPath) {
          const videoTypeName = selectedVideoType === 'enhanced' ? 'Enhanced' : 'Stitched';
          return { success: false, error: `${videoTypeName} video not available for upload` };
        }

        // Check for duplicate pending/uploading uploads (only block if same video type is already uploading)
        const existingResult = await this.getUploadsForHighlight(entityId);
        if (existingResult.success && existingResult.data) {
          const pending = existingResult.data.find(
            u => (u.upload_status === 'pending' || u.upload_status === 'uploading') &&
                 u.metadata?.videoType === selectedVideoType
          );
          if (pending) {
            const videoTypeName = selectedVideoType === 'enhanced' ? 'Enhanced' : 'Stitched';
            return { success: false, error: `${videoTypeName} video upload already in progress` };
          }
        }

        // Include videoType in metadata for tracking
        const metadataWithVideoType = {
          ...metadata,
          videoType: selectedVideoType
        };

        // Create upload record
        const { data: upload, error: uploadError } = await supabase
          .from('social_media_uploads')
          .insert({
            highlight_id: entityId,
            video_id: null,
            match_id: normalizedMatchId,
            org_id: orgId,
            social_media_integration_id: integrationId,
            platform: platform,
            metadata: metadataWithVideoType,
            upload_status: 'pending'
          })
          .select()
          .single();

        if (uploadError || !upload) {
          return {
            success: false,
            error: uploadError?.message || 'Failed to create upload record'
          };
        }

        uploadId = upload.id;

        // Create job in video_processing_jobs
        const jobResult = await videoProcessingJobsService.createJob({
          video_id: null,
          highlight_id: entityId,
          job_type: 'social_media_upload',
          payload: {
            entityType: 'highlight',
            entityId: entityId,
            orgId,
            platform,
            integrationId,
            metadata,
            uploadId: upload.id,
            videoType: selectedVideoType
          }
        });

        if (!jobResult.success || !jobResult.data) {
          // Rollback: Delete upload record if job creation fails
          if (uploadId) {
            await supabase
              .from('social_media_uploads')
              .delete()
              .eq('id', uploadId);
          }
          return {
            success: false,
            error: jobResult.error || 'Failed to create upload job'
          };
        }

        return {
          success: true,
          data: { uploadId: upload.id, jobId: jobResult.data.id }
        };

      } else if (entityType === 'video') {
        // Video validation logic
        const videoResult = await videosService.getVideoById(entityId);
        if (!videoResult.success || !videoResult.data) {
          return { success: false, error: 'Video not found' };
        }

        const video = videoResult.data;

        // Only videos with status 'ready' (transcoded) can be uploaded
        if (video.status !== 'ready') {
          return { success: false, error: 'Video must be transcoded before uploading' };
        }

        // Check if transcoded master URL is available
        if (!video.public_url) {
          return { success: false, error: 'Video transcoded master URL not available' };
        }

        // Check for duplicate pending/uploading uploads
        const existingResult = await this.getUploadsForVideo(entityId);
        if (existingResult.success && existingResult.data) {
          const pending = existingResult.data.find(
            u => u.upload_status === 'pending' || u.upload_status === 'uploading'
          );
          if (pending) {
            return { success: false, error: 'Video upload already in progress' };
          }
        }

        // Create upload record
        const { data: upload, error: uploadError } = await supabase
          .from('social_media_uploads')
          .insert({
            highlight_id: null,
            video_id: entityId,
            match_id: normalizedMatchId,
            org_id: orgId,
            social_media_integration_id: integrationId,
            platform: platform,
            metadata: metadata,
            upload_status: 'pending'
          })
          .select()
          .single();

        if (uploadError || !upload) {
          return {
            success: false,
            error: uploadError?.message || 'Failed to create upload record'
          };
        }

        uploadId = upload.id;

        // Create job in video_processing_jobs
        const jobResult = await videoProcessingJobsService.createJob({
          video_id: entityId,
          highlight_id: null,
          job_type: 'social_media_upload',
          payload: {
            entityType: 'video',
            entityId: entityId,
            orgId,
            platform,
            integrationId,
            metadata,
            uploadId: upload.id
          }
        });

        if (!jobResult.success || !jobResult.data) {
          // Rollback: Delete upload record if job creation fails
          if (uploadId) {
            await supabase
              .from('social_media_uploads')
              .delete()
              .eq('id', uploadId);
          }
          return {
            success: false,
            error: jobResult.error || 'Failed to create upload job'
          };
        }

        return {
          success: true,
          data: { uploadId: upload.id, jobId: jobResult.data.id }
        };
      } else {
        return { success: false, error: 'Invalid entity type' };
      }
    } catch (error) {
      // Rollback on any error
      if (uploadId) {
        await supabase
          .from('social_media_uploads')
          .delete()
          .eq('id', uploadId);
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get upload status
   */
  async getUploadStatus(uploadId: string): Promise<ServiceResult<SocialMediaUpload>> {
    try {
      const { data, error } = await supabase
        .from('social_media_uploads')
        .select('*')
        .eq('id', uploadId)
        .single();

      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Upload not found'
        };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getUploadStatus failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get uploads for highlight
   */
  async getUploadsForHighlight(
    highlightId: string
  ): Promise<ServiceResult<SocialMediaUpload[]>> {
    try {
      const { data, error } = await supabase
        .from('social_media_uploads')
        .select(`
          *,
          social_media_integrations (
            display_name,
            platform_account_name
          )
        `)
        .eq('highlight_id', highlightId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getUploadsForHighlight failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get uploads for video
   */
  async getUploadsForVideo(
    videoId: string
  ): Promise<ServiceResult<SocialMediaUpload[]>> {
    try {
      const { data, error } = await supabase
        .from('social_media_uploads')
        .select(`
          *,
          social_media_integrations (
            display_name,
            platform_account_name
          )
        `)
        .eq('video_id', videoId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getUploadsForVideo failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Check if entity has active uploads
   * Used to prevent setting is_active = false when uploads exist
   */
  async hasActiveUploads(
    entityId: string,
    entityType: 'highlight' | 'video'
  ): Promise<ServiceResult<boolean>> {
    try {
      const query = supabase
        .from('social_media_uploads')
        .select('id')
        .limit(1);

      if (entityType === 'highlight') {
        query.eq('highlight_id', entityId);
      } else {
        query.eq('video_id', entityId);
      }

      // Check for uploads that are not cancelled or failed permanently
      const activeStatuses = ['pending', 'queued', 'uploading', 'processing', 'completed'];
      query.in('upload_status', activeStatuses);

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: (data?.length || 0) > 0 };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('hasActiveUploads failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get uploads for organization
   */
  async getUploadsForOrg(
    orgId: string,
    filters?: { platform?: string; status?: string }
  ): Promise<ServiceResult<SocialMediaUpload[]>> {
    try {
      let query = supabase
        .from('social_media_uploads')
        .select(`
          *,
          social_media_integrations (
            display_name,
            platform_account_name
          ),
          video_highlights (
            name,
            output_url
          )
        `)
        .eq('org_id', orgId);

      if (filters?.platform) {
        query = query.eq('platform', filters.platform);
      }

      if (filters?.status) {
        query = query.eq('upload_status', filters.status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('getUploadsForOrg failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Delete upload record
   */
  async deleteUpload(uploadId: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('social_media_uploads')
        .delete()
        .eq('id', uploadId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('deleteUpload failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Delete/disconnect an org-level integration
   */
  async deleteOrgIntegration(
    integrationId: string,
    orgId: string
  ): Promise<ServiceResult<void>> {
    try {
      // Verify the integration belongs to the org and is not parent-level
      const { data: integration, error: fetchError } = await supabase
        .from('social_media_integrations')
        .select('id, org_id, parent_level')
        .eq('id', integrationId)
        .single();

      if (fetchError || !integration) {
        return { success: false, error: 'Integration not found' };
      }

      if (integration.parent_level) {
        return { success: false, error: 'Cannot delete parent-level integration' };
      }

      if (integration.org_id !== orgId) {
        return { success: false, error: 'Integration does not belong to this organization' };
      }

      // Check if there are any active uploads using this integration
      const { data: activeUploads } = await supabase
        .from('social_media_uploads')
        .select('id')
        .eq('social_media_integration_id', integrationId)
        .in('upload_status', ['pending', 'queued', 'uploading', 'processing'])
        .limit(1);

      if (activeUploads && activeUploads.length > 0) {
        return {
          success: false,
          error: 'Cannot delete integration with active uploads. Please wait for uploads to complete or cancel them first.'
        };
      }

      // Delete the integration
      const { error: deleteError } = await supabase
        .from('social_media_integrations')
        .delete()
        .eq('id', integrationId);

      if (deleteError) {
        return { success: false, error: deleteError.message || 'Failed to delete integration' };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('deleteOrgIntegration failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Retry failed upload (uses existing retry-job edge function)
   */
  async retryUpload(uploadId: string): Promise<ServiceResult<void>> {
    try {
      // Get upload record
      const uploadResult = await this.getUploadStatus(uploadId);
      if (!uploadResult.success || !uploadResult.data) {
        return { success: false, error: 'Upload not found' };
      }

      const upload = uploadResult.data;

      // Build query based on entity type
      let query = supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('job_type', 'social_media_upload')
        .eq('is_active', true);

      // Query by highlight_id or video_id based on upload record
      if (upload.highlight_id) {
        query = query.eq('highlight_id', upload.highlight_id);
      } else if (upload.video_id) {
        query = query.eq('video_id', upload.video_id);
      } else {
        return { success: false, error: 'Upload record missing entity reference' };
      }

      const { data: jobs, error: jobsError } = await query.order('created_at', { ascending: false });

      if (jobsError || !jobs || jobs.length === 0) {
        return { success: false, error: 'Job not found' };
      }

      // Find job with matching uploadId in payload
      const job = jobs.find(
        j => j.payload?.uploadId === uploadId
      );

      if (!job) {
        return { success: false, error: 'Job not found for this upload' };
      }

      // Call retry-job edge function
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        return { success: false, error: 'Supabase URL not configured' };
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/retry-job`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jobId: job.id })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { success: false, error: errorData.error || 'Failed to retry upload' };
      }

      // Update upload status
      await supabase
        .from('social_media_uploads')
        .update({
          upload_status: 'pending',
          error_message: null,
          retry_count: uploadResult.data.retry_count + 1
        })
        .eq('id', uploadId);

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('retryUpload failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }
}

export const socialMediaService = new SocialMediaService();

